import * as React from 'react';
import * as _ from 'lodash';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, MatchActionCreators, IEvaluateRecipientDonorResponse, DonorPreselectActionCreators, IRequestExmResponse, IAppState, FileActionCreators, DonorActionCreators, IAmbraDirectToViewerResponse } from '@nkr/store/index';
import { IRecipient, IDonor, ICompatibilityResult, ILkdpiResults, IDonorPreselectNote, ISerologyTest, IBaseResponse, ICenterAddress, IState as IStateItem, IContact, ISingleEpletMmRisk, IListItem } from '@nkr/models/index';
import { FileDocumentType, FileDocumentTypeDescription, MatchOfferDeclineReason, MortalityMatchType } from '@nkr/enums/index';
import { toast } from 'react-toastify';
import { Utility } from '@nkr/services/utility';
import { downloadFile } from '@nkr/services/download-file';
import Match from './match';
import RecipientNav from '../recipients/recipient-nav';
import HardBlock from './modals/hard-block';
import InfoRequest from './modals/info-request';
import ExmRequest from './modals/exm-request';

interface IProps {
    recipientId: number,
    donorId: number,
    centerId: number,
    onUpdateRecipientAlias: (alias: string) => void
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getRecipientDonor: (recipientId: number, donorId: number, centerId: number) => Promise<IEvaluateRecipientDonorResponse>,
    acceptDonor: (recipientId: number, donorId: number, isFullyConfident: boolean, hasMortalityRisk: boolean) => Promise<IBaseResponse>,
    declineDonor: (recipientId: number, recipientCenterId: number, donorId: number, hardBlockSource: string, declineReasons: { reasonId: number, otherText: string }[], isGlobalDecline: boolean, hasMortalityRisk: boolean) => Promise<IBaseResponse>,
    requestInfo: (recipientId: number, donorId: number, message: string) => Promise<IBaseResponse>,
    requestExm: (recipientId: number, donorId: number, message: string, allowDuplicate: boolean, freshBloodOverride: boolean) => Promise<IRequestExmResponse>,
    saveNotes: (recipientCenterId: number, donorId: number, recipientId: number, note: string) => Promise<IBaseResponse>,
    downloadFile: (docType: number, lookupId: number) => Promise<any>,
    getAmbraLink: (donorId: number) => Promise<IAmbraDirectToViewerResponse>,
}

type TProps =
    IProps
    & IStateProps
    & IDispatchProps;

interface IState {
    recipient: IRecipient,
    donor: IDonor,
    compatibilityResult: ICompatibilityResult,
    lkdpiResults: ILkdpiResults,
    compatiblePairLkdpiResults: ILkdpiResults,
    singleEpletMmRisk: ISingleEpletMmRisk,
    isRecipientCenter: boolean,
    recipientCenterContacts: IContact[],
    isDonorCenter: boolean,
    donorCenterContacts: IContact[],
    donorNote: IDonorPreselectNote,
    donorNotesContent: string,
    matchNote: IDonorPreselectNote,
    matchNotesContent: string,
    donorKidneyPickupAddress: ICenterAddress,
    donorKidneyShipToAddress: ICenterAddress,
    recipientKidneyPickupAddress: ICenterAddress,
    recipientKidneyShipToAddress: ICenterAddress,
    swapNumber: string,
    serologyTestList: ISerologyTest[],
    lookupData: {
        stateList: IStateItem[],
        hardBlockReasons: IListItem[]
    },
    doesStudyExist: number,
    centerAddress: {
        show: boolean,
        data: ICenterAddress
    },
    showSerologyTests: boolean,
    hasOutstandingOffer: boolean,
    declineDonor: {
        show: boolean,
        isGlobalDecline: boolean
    },
    showInfoRequest: boolean,
    hasExistingXm: boolean,
    existingXmStatus: string,
    requestExm: {
        show: boolean,
        freshBloodOverride: boolean
    },
    allowDuplicateXm: boolean,
    matchHistoryId: number
}

class EvaluateDonor extends React.Component<TProps, IState> {
    readonly state: IState = {
        recipient: null,
        donor: null,
        compatibilityResult: null,
        lkdpiResults: null,
        compatiblePairLkdpiResults: null,
        singleEpletMmRisk: null,
        isRecipientCenter: false,
        recipientCenterContacts: [],
        isDonorCenter: false,
        donorCenterContacts: [],
        donorNote: null,
        donorNotesContent: '',
        matchNote: null,
        matchNotesContent: '',
        donorKidneyPickupAddress: null,
        donorKidneyShipToAddress: null,
        recipientKidneyPickupAddress: null,
        recipientKidneyShipToAddress: null,
        swapNumber: '',
        serologyTestList: [],
        lookupData: {
            stateList: [],
            hardBlockReasons: []
        },
        doesStudyExist: 0,
        centerAddress: {
            show: false,
            data: null
        },
        showSerologyTests: false,
        hasOutstandingOffer: false,
        declineDonor: {
            show: false,
            isGlobalDecline: false
        },
        showInfoRequest: false,
        hasExistingXm: false,
        existingXmStatus: '',
        requestExm: {
            show: false,
            freshBloodOverride: false
        },
        allowDuplicateXm: true,
        matchHistoryId: 0
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting donor & recipient information, one moment please...').then(() => {
            this.props.getRecipientDonor(this.props.recipientId, this.props.donorId, this.props.centerId)
                .then(response => {
                    if (response.data.success) {
                        let {
                            recipient, donor,
                            compatibilityResult, lkdpiResults, compatiblePairLkdpiResults, singleEpletMmRisk,
                            isRecipientCenter, recipientCenterContacts,
                            isDonorCenter, donorCenterContacts,
                            donorNote, matchNote,
                            donorKidneyPickupAddress, donorKidneyShipToAddress,
                            recipientKidneyPickupAddress, recipientKidneyShipToAddress,
                            serologyTestList,
                            doesStudyExist,
                            hasExistingXm, existingXmStatus,
                            hasOutstandingOffer,
                            outstandingOfferId,
                            swapNumber,
                            lookupData
                        } = response.data.results;

                        this.setState({
                            recipient,
                            donor,
                            compatibilityResult,
                            lkdpiResults,
                            compatiblePairLkdpiResults,
                            singleEpletMmRisk,
                            isRecipientCenter,
                            recipientCenterContacts,
                            isDonorCenter,
                            donorCenterContacts,
                            donorNote,
                            donorNotesContent: donorNote != null ? donorNote.note : '',
                            matchNote,
                            matchNotesContent: matchNote != null ? matchNote.note : '',
                            donorKidneyPickupAddress, donorKidneyShipToAddress,
                            recipientKidneyPickupAddress, recipientKidneyShipToAddress,
                            serologyTestList,
                            doesStudyExist,
                            lookupData,
                            hasExistingXm,
                            existingXmStatus,
                            hasOutstandingOffer,
                            swapNumber,
                            matchHistoryId: outstandingOfferId
                        }, () => {
                            this.props.onUpdateRecipientAlias(this.state.recipient.alias);
                        });
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleDonorNotesChange = (donorNotesContent: string) => {
        this.setState({
            donorNotesContent
        });
    }

    handleMatchNotesChange = (matchNotesContent: string) => {
        this.setState({
            matchNotesContent
        });
    }

    handleSaveNotes = (isMatch: boolean) => {
        let recipientCenterId = this.state.recipient.centerId;
        let donorId = this.state.donor.id;
        let recipientId = isMatch ? this.state.recipient.id : null;
        let notes = isMatch ? this.state.matchNotesContent : this.state.donorNotesContent;
        let loadingDescription = isMatch ? 'Saving match notes, one moment please...' : 'Saving donor notes, one moment please';
        let messagePrefix = isMatch ? 'Match notes ' : 'Donor notes ';

        this.props.showLoading(loadingDescription).then(() => {
            this.props.saveNotes(recipientCenterId, donorId, recipientId, notes)
                .then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success(messagePrefix + ' saved successfully');
                    }
                })
                .catch(error => {
                    toast.error('Unable to save ' + messagePrefix.toLowerCase());
                });
        });
    }

    handleAcceptDonor = (isFullyConfident: boolean) => {
        let { recipientId, donorId } = this.props;
        let { hasMortalityRisk } = this.state.recipient;

        this.props.showLoading('Updating preselect, one moment please...').then(() => {
            this.props.acceptDonor(recipientId, donorId, isFullyConfident, hasMortalityRisk).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    toast.success('Preselect Completed successfully: Accepted ' + (isFullyConfident ? 'Fully Confident' : 'Not Fully Confident'));
                }
            });
        });
    }

    handleShowDeclineDonor = (isGlobalDecline: boolean) => {
        this.setState({
            declineDonor: {
                show: true,
                isGlobalDecline
            }
        });
    }

    handleDeclineDonor = (ids: number[], otherText: string) => {
        const otherTypes = [MatchOfferDeclineReason.DonorOther, MatchOfferDeclineReason.SurgeryDateOther, MatchOfferDeclineReason.PatientDeactivateOther] as number[];

        let { recipientId, donorId } = this.props;
        let { hasMortalityRisk } = this.state.recipient;
        let recipientCenterId = this.state.recipient.centerId;
        let isGlobalDecline = this.state.declineDonor.isGlobalDecline;

        let declineReasons = ids.map(x => {
            return { reasonId: x, otherText: otherText && otherTypes.includes(x) ? otherText : null }
        })

        this.setState({
            declineDonor: {
                show: false,
                isGlobalDecline: false
            }
        }, () => {
            this.props.showLoading('Updating preselect, one moment please...').then(() => {
                this.props.declineDonor(recipientId, recipientCenterId, donorId, 'Pre-Select', declineReasons, isGlobalDecline, hasMortalityRisk).then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success('Preselect Completed successfully: Declined');
                    }
                });
            });
        });
    }

    handleCancelDeclineDonor = () => {
        this.setState({
            declineDonor: {
                show: false,
                isGlobalDecline: false
            }
        });
    }

    handleShowRequestInfo = () => {
        this.setState({
            showInfoRequest: true
        });
    }

    handleRequestInfo = (message: string) => {
        let { recipientId, donorId } = this.props;

        this.setState({
            showInfoRequest: false
        }, () => {
            this.props.showLoading('Sending info request, one moment please...').then(() => {
                this.props.requestInfo(recipientId, donorId, message).then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success('Info request sent successfully');
                    }
                });
            });
        });
    }

    handleCancelRequestInfo = () => {
        this.setState({
            showInfoRequest: false
        });
    }

    handleShowRequestExploratoryXm = (freshBloodOverride: boolean) => {
        this.setState({
            requestExm: {
                show: true,
                freshBloodOverride
            }
        });
    }

    handleRequestExploratoryXm = () => {
        let { recipientId, donorId } = this.props;
        let allowDuplicateXm = this.state.allowDuplicateXm;
        let freshBloodOverride = this.state.requestExm.freshBloodOverride;

        this.setState({
            requestExm: {
                show: false,
                freshBloodOverride: false
            },
            allowDuplicateXm: false
        }, () => {
            this.props.showLoading('Sending exploratory XM request, one moment please...').then(() => {
                this.props.requestExm(recipientId, donorId, '', allowDuplicateXm, freshBloodOverride).then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        let { xmId, orderNumber } = response.data.results;

                        let message = orderNumber != '' ? 'EXM request sent successfully (' + xmId + '): PathTec Order ' + orderNumber : 'EXM request sent successfully (' + xmId + ')';

                        toast.success(message);
                    } else {
                        toast.error("Unable to complete request: " + response.data.message);
                    }
                });
            });
        });
    }

    handleCancelRequestExploratoryXm = () => {
        this.setState({
            requestExm: {
                show: false,
                freshBloodOverride: false
            },
        });
    }

    handleGetFile = (docType: FileDocumentType, donorId: number) => {
        this.props.downloadFile(docType, donorId).then((response: any) => {
            downloadFile(response.data, `${FileDocumentTypeDescription[docType]}.pdf`, response.headers.contentType)
        }).catch((error: IBaseResponse) => {
            toast.error("Unable to download file at this time.  Please contact support.");
        })
    }

    handleViewCtScan = (donorId: number) => {
        this.props.getAmbraLink(donorId).then(response => {
            window.open(response.data.results, "_blank");
        });
    }

    render = () => {
        if (this.state.recipient == null || this.state.donor == null)
            return <div style={{ padding: '0px 5px' }}>{Utility.getWaitMessage()}...</div>;

        return (
            <div style={{ padding: '0px 5px' }}>
                <RecipientNav
                    activeKey='EVALUATE-DONOR'
                    isAdmin={false}
                    isNkrLogin={false}
                    centerId={this.props.centerId}
                    recipientAlias={this.state.recipient.alias}
                    recipientId={this.props.recipientId}
                    donorId={this.props.donorId}
                />

                <div style={{ width: "100%", minHeight: "65vh", paddingTop: 20, paddingLeft: 5, paddingRight: 5, marginBottom: 5, backgroundColor: "#FFF" }}>
                    <Match
                        donor={this.state.donor}
                        recipient={this.state.recipient}
                        compatibilityResult={this.state.compatibilityResult}
                        lkdpiResults={this.state.lkdpiResults}
                        compatiblePairLkdpiResults={this.state.compatiblePairLkdpiResults}
                        singleEpletMmRisk={this.state.singleEpletMmRisk}
                        donorNote={this.state.donorNote}
                        donorNotesContent={this.state.donorNotesContent}
                        matchNote={this.state.matchNote}
                        matchNotesContent={this.state.matchNotesContent}
                        includeButtons={true}
                        doesStudyExist={this.state.doesStudyExist}
                        hasOutstandingOffer={this.state.hasOutstandingOffer}
                        isRecipientCenter={this.state.isRecipientCenter}
                        showNotes={true}
                        isNkrLogin={this.props.app.user.group.isSuper}
                        swapNumber={this.state.swapNumber}
                        matchHistoryId={this.state.matchHistoryId}
                        onDonorNotesChange={this.handleDonorNotesChange}
                        onMatchNodesChange={this.handleMatchNotesChange}
                        onSaveNotes={this.handleSaveNotes}
                        onAcceptDonor={this.handleAcceptDonor}
                        onDeclineDonor={this.handleShowDeclineDonor}
                        onRequestInfo={this.handleShowRequestInfo}
                        onRequestExploratoryXm={this.handleShowRequestExploratoryXm}
                        onGetFile={this.handleGetFile}
                        onViewCtScan={this.handleViewCtScan}
                    />
                </div>

                <HardBlock
                    show={this.state.declineDonor.show}
                    alias={this.state.donor.alias}
                    centerAlias={this.state.donor.center.alias}
                    isGlobalDecline={this.state.declineDonor.isGlobalDecline}
                    warnOnHighDeclineRatio={this.state.recipient.warnOnHighDeclineRatio}
                    hasMortalityRisk={this.state.recipient.hasMortalityRisk}
                    mortalityRiskPercent={this.state.recipient.mortalityRiskPercent}
                    hardBlockReasons={this.state.lookupData.hardBlockReasons}
                    onDeclineDonor={this.handleDeclineDonor}
                    onCancel={this.handleCancelDeclineDonor}
                />

                <InfoRequest
                    show={this.state.showInfoRequest}
                    onConfirmRequest={this.handleRequestInfo}
                    onCancel={this.handleCancelRequestInfo}
                />

                <ExmRequest
                    show={this.state.requestExm.show}
                    hasExistingXm={this.state.hasExistingXm}
                    existingXmStaus={this.state.existingXmStatus}
                    onConfirmRequest={this.handleRequestExploratoryXm}
                    onCancel={this.handleCancelRequestExploratoryXm}
                />
            </div>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getRecipientDonor: (recipientId, donorId, centerId) => {
            return dispatch(MatchActionCreators.getRecipientDonor(recipientId, donorId, centerId));
        },
        acceptDonor: (recipientId, donorId, isFullyConfident, hasMortalityRisk) => {
            return dispatch(DonorPreselectActionCreators.acceptDonor(recipientId, donorId, isFullyConfident, hasMortalityRisk, MortalityMatchType.MatchOffer));
        },
        declineDonor: (recipientId, recipientCenterId, donorId, hardBlockSource, hardBlockReasons, isGlobalDecline, hasMortalityRisk) => {
            return dispatch(DonorPreselectActionCreators.declineDonor(recipientId, recipientCenterId, donorId, hardBlockSource, hardBlockReasons, isGlobalDecline, hasMortalityRisk, MortalityMatchType.MatchOffer));
        },
        requestInfo: (recipientId, donorId, message) => {
            return dispatch(DonorPreselectActionCreators.requestInfo(recipientId, donorId, message));
        },
        requestExm: (recipientId, donorId, message, allowDuplicate, freshBloodOverride) => {
            return dispatch(DonorPreselectActionCreators.requestExm(recipientId, donorId, message, allowDuplicate, freshBloodOverride));
        },
        saveNotes: (recipientCenterId, donorId, recipientId, note) => {
            return dispatch(DonorPreselectActionCreators.saveNotes(recipientCenterId, donorId, recipientId, note));
        },
        downloadFile: (docType, id) => {
            return dispatch(FileActionCreators.downloadFile(docType, id))
        },
        getAmbraLink: (donorId) => {
            return dispatch(DonorActionCreators.getAmbraLink(donorId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(EvaluateDonor)